import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StepIndicator from './StepIndicator';
import './UploadSteps.css';

const UploadSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const lastUpload = location.state?.lastUpload;
    const pets = Array.isArray(location.state?.pets) ? location.state.pets : [];
    const extractedPatient = location.state?.extractedPatient || null;
    const documentType = (lastUpload?.documentType || 'upload').toLowerCase();
    const redactionBoxCount = lastUpload?.privacyReview?.redactionBoxCount || 0;
    const nextUploadLabel = 'Upload another document';
    const browseLabel = 'Browse vet bills';

    // Pet profile suggestion — only when the upload isn't already attached.
    const [petPrompt, setPetPrompt] = useState(() => Boolean(lastUpload?.billId && !lastUpload?.petId));
    const [petName, setPetName] = useState(extractedPatient?.name || '');
    const [petSpecies, setPetSpecies] = useState(extractedPatient?.species || '');
    const [petBreed, setPetBreed] = useState(extractedPatient?.breed || '');
    const [petSaving, setPetSaving] = useState(false);
    const [petMessage, setPetMessage] = useState('');
    const [petAttached, setPetAttached] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(pets.length === 0);

    const authHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const attachBillToPet = async (petId) => {
        const response = await fetch(`/api/vetbills/${lastUpload.billId}/pet`, {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ petId })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Unable to attach the upload to this pet.');
        }
    };

    const handleAttachExisting = async (pet) => {
        setPetSaving(true);
        setPetMessage('');
        try {
            await attachBillToPet(pet._id);
            setPetAttached(true);
            setPetMessage(`Attached to ${pet.name}. Future uploads can build ${pet.name}'s cost history.`);
        } catch (error) {
            setPetMessage(error.message);
        } finally {
            setPetSaving(false);
        }
    };

    const handleCreateAndAttach = async (event) => {
        event.preventDefault();
        if (!petName.trim()) {
            setPetMessage('Please give the pet a name.');
            return;
        }
        setPetSaving(true);
        setPetMessage('');
        try {
            const response = await fetch('/api/pets', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ name: petName.trim(), species: petSpecies.trim(), breed: petBreed.trim() })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to create the pet profile.');
            }
            await attachBillToPet(data.pet._id);
            setPetAttached(true);
            setPetMessage(`Profile created — this ${documentType} is now part of ${data.pet.name}'s private cost history.`);
        } catch (error) {
            setPetMessage(error.message);
        } finally {
            setPetSaving(false);
        }
    };

    if (!lastUpload) {
        return (
            <div className="upload-steps-container success-container">
                <h2>Upload saved</h2>
                <p className="muted-text">Your upload was saved. You can upload another document or browse vet bills.</p>
                <div className="success-actions">
                    <button type="button" className="next-btn" onClick={() => navigate('/upload/step1')}>{nextUploadLabel}</button>
                    <button type="button" className="secondary-btn" onClick={() => navigate('/browse')}>{browseLabel}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-steps-container success-container">
            <StepIndicator currentStep={5} />

            <div className="success-hero">
                <div className="success-checkmark" aria-hidden="true">✓</div>
                <div>
                    <h2>Upload saved</h2>
                    <p className="muted-text">
                        {lastUpload.visibility === 'private'
                            ? `Saved to your private records. This ${documentType} is visible only to you.`
                            : `Thanks for contributing. This ${documentType} is now available to help compare vet pricing.`}
                    </p>
                    {lastUpload.privacyReviewed && (
                        <p className="privacy-success-note">
                            {redactionBoxCount ? `You hid ${redactionBoxCount} personal detail${redactionBoxCount === 1 ? '' : 's'} before saving.` : 'You checked it for personal info before saving.'}
                        </p>
                    )}
                </div>
            </div>

            {petPrompt && (
                <section className="review-card pet-suggestion-card" aria-label="Pet profile suggestion">
                    {petAttached ? (
                        <p className="pet-suggestion-done">{petMessage}</p>
                    ) : (
                        <>
                            <div className="card-heading-row">
                                <div>
                                    <h3>
                                        {extractedPatient?.name
                                            ? `Track this for ${extractedPatient.name}?`
                                            : 'Track this for your pet?'}
                                    </h3>
                                    <p className="muted-text">
                                        Attach uploads to a pet profile to build a private history of visits and costs. Pet profiles are never shown publicly.
                                    </p>
                                </div>
                                <button type="button" className="pet-suggestion-dismiss" onClick={() => setPetPrompt(false)} aria-label="Dismiss">
                                    Not now
                                </button>
                            </div>

                            {pets.length > 0 && !showCreateForm && (
                                <div className="pet-suggestion-existing">
                                    {pets.map(pet => (
                                        <button
                                            key={pet._id}
                                            type="button"
                                            className="secondary-btn"
                                            disabled={petSaving}
                                            onClick={() => handleAttachExisting(pet)}
                                        >
                                            Attach to {pet.name}
                                        </button>
                                    ))}
                                    <button type="button" className="secondary-btn" disabled={petSaving} onClick={() => setShowCreateForm(true)}>
                                        + New pet
                                    </button>
                                </div>
                            )}

                            {showCreateForm && (
                                <form className="pet-suggestion-form" onSubmit={handleCreateAndAttach}>
                                    <label>
                                        <span className="field-label">Name</span>
                                        <input value={petName} onChange={e => setPetName(e.target.value)} placeholder="Pet name" />
                                    </label>
                                    <label>
                                        <span className="field-label">Species</span>
                                        <input value={petSpecies} onChange={e => setPetSpecies(e.target.value)} placeholder="Dog, cat..." />
                                    </label>
                                    <label>
                                        <span className="field-label">Breed (optional)</span>
                                        <input value={petBreed} onChange={e => setPetBreed(e.target.value)} placeholder="Breed" />
                                    </label>
                                    <button type="submit" className="next-btn" disabled={petSaving || !petName.trim()}>
                                        {petSaving ? 'Saving...' : 'Create profile & attach'}
                                    </button>
                                </form>
                            )}

                            {petMessage && !petAttached && <p className="pet-suggestion-error">{petMessage}</p>}
                        </>
                    )}
                </section>
            )}

            <div className="success-actions">
                <button type="button" className="next-btn" onClick={() => navigate('/upload/step1')}>{nextUploadLabel}</button>
                {lastUpload.billId && (
                    <button type="button" className="secondary-btn" onClick={() => navigate(`/bills/${lastUpload.billId}`)}>View your saved {documentType}</button>
                )}
                <button type="button" className="secondary-btn" onClick={() => navigate('/browse')}>{browseLabel}</button>
            </div>
        </div>
    );
};

export default UploadSuccess;
